import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { bool, func, object, number, string, array } from 'prop-types';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import { PAGES } from '../../../helpers/enums';
import { createResourceLocatorString } from '../../../util/routes';
import { createSlug } from '../../../util/urlHelpers';
import { propTypes } from '../../../util/types';
import { FormattedMessage, intlShape } from '../../../util/reactIntl';
import {
  isArrayLength,
  sortWebinarEventsByDate,
  switchAppLanguage,
} from '../../../util/genericHelpers';

import englishImage from '../../LocaleMenu/flags/english.png';
import greekImage from '../../LocaleMenu/flags/greek.png';
import germanImage from '../../LocaleMenu/flags/german.png';
import {
  canAccessDashboard,
  isClientUser,
  isCorporateUser as isCorporateUserType,
  isIndividualUser,
  isUsersStripeConnected,
  hasPremiumContentAccess,
} from '../../../util/dataExtractors';
import routeConfiguration, {
  ACCOUNT_SETTINGS_PAGES,
  draftId,
  draftSlug,
} from '../../../routing/routeConfiguration';
import {
  InlineTextButton,
  LinkedLogo,
  NamedLink,
  IconAccordion,
  IconHeart,
  IconLogout,
  IconSettings,
  IconSessions,
  IconShield,
  IconWatched,
  IconSymptomsTracker,
  IconMoodsTracker,
  IconHabitsTracker,
  Button,
} from '../../../components';
import LocaleMenu from '../../LocaleMenu/LocaleMenu';
import IconArrowRight from '../../IconArrowRight/IconArrowRight';
import Icons from '../../Icons/Icons';
import IconClose from '../../IconClose/IconClose';

import css from './TopbarDesktop.module.css';
import GlobalSearch from '../GlobalSearch/GlobalSearch';
import SearchPanel from '../GlobalSearch/SearchPanel';
import {
  trackArticleCategoryClicked,
  trackChatbotEvent,
  trackLogInClicked,
  trackLogOutClicked,
  trackMoodSymptomHabitEvent,
  trackSessionsClicked,
  trackWellnessProgramClicked,
} from '../../../util/mixpanelHelpers';
import { MIXPANEL_EVENTS, TOPBAR_GUIDE_OPTIONS } from '../../../util/enums';
import IconWebsite from '../../IconWebsite/IconWebsite';

import { useSignInContext } from '../../../context/SignInContext';

const logTopbarPremiumAccess = payload => {
  if (process.env.REACT_APP_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[TopbarPremiumAccess]', payload);
  }
};

const TopbarDesktop = props => {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isSubscriptionExpanded, setIsSubscriptionExpanded] = useState(false);
  const [isWrittenMenuOpen, setIsWrittenMenuOpen] = useState(false);
  const [writtenMenuTimeout, setWrittenMenuTimeout] = useState(null);
  const [isProfileMenuHoverOpen, setIsProfileMenuHoverOpen] = useState(false);
  const [profileMenuTimeout, setProfileMenuTimeout] = useState(null);
  const profileMenuContentRef = useRef(null);

  const {
    className,
    appConfig,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    intl,
    isAuthenticated,
    onLogout,
    isFullWidthHeader,
    headerWithoutShadow,
    isHeaderSticky,
    staticPageHeader,
    dashboardHeader,
    ownListing,
    logoVariant,
    events,
    viewport,
    onSubmitLogin,
    loginInProgress,
    loginError,
  } = props;

  const { openSignIn } = useSignInContext();

  const location = useLocation();
  const history = useHistory();
  const isStripeConnected = isUsersStripeConnected(currentUser);
  const isCorporateUser = isCorporateUserType(currentUser);
  const pathname = location?.pathname || '';
  const isRoutePath = path => pathname === path || pathname.endsWith(path);

  const handleCloseProfileMenu = () => {
    setIsProfileMenuOpen(false);
    setIsProfileMenuHoverOpen(false);
  };

  // Keep the profile menu inside the viewport on desktop (LTR & RTL)
  useLayoutEffect(() => {
    const menuEl = profileMenuContentRef.current;
    if (!menuEl || !isProfileMenuHoverOpen) {
      if (menuEl) menuEl.style.removeProperty('--profile-menu-shift');
      return undefined;
    }

    const padding = 12; // breathing room from viewport edges

    const updatePosition = () => {
      const rect = menuEl.getBoundingClientRect();
      const overflowRight = rect.right - (window.innerWidth - padding);
      const overflowLeft = padding - rect.left;

      let shift = 0;
      if (overflowRight > 0) shift -= overflowRight;
      if (overflowLeft > 0) shift += overflowLeft;

      menuEl.style.setProperty('--profile-menu-shift', `${shift}px`);
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('resize', updatePosition);
    };
  }, [isProfileMenuHoverOpen]);

  const handleSubscriptionToggle = () => {
    setIsSubscriptionExpanded(!isSubscriptionExpanded);
  };

  const isDoctorsPage = isRoutePath(PAGES.DOCTORS_PAGE);
  const isCompaniesPage = isRoutePath(PAGES.COMPANIES_PAGE);
  const isAiPage = isRoutePath(PAGES.AI_PAGE);
  const isAboutPage = isRoutePath(PAGES.ABOUT_PAGE);
  const isHealthPlansPage = isRoutePath('/health-plans');
  const isHospitalsPage = isRoutePath('/hospitals-and-clinics');
  const isSignupPage = [PAGES.AUTH_DOCTOR_PAGE, PAGES.AUTH_INDIVIDUAL_PAGE].includes(
    location?.pathname
  );

  const [mounted, setMounted] = useState(false);

  const marketplaceName = appConfig.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  useEffect(() => {
    setMounted(true);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (writtenMenuTimeout) {
        clearTimeout(writtenMenuTimeout);
      }
      if (profileMenuTimeout) {
        clearTimeout(profileMenuTimeout);
      }
    };
  }, [writtenMenuTimeout, profileMenuTimeout]);

  const handleLanguageChange = locale => {
    switchAppLanguage({ locale, pathname: '/home' });
  };

  const classes = classNames(rootClassName || css.root, className, {
    [css.stickNav]: isHeaderSticky,
    [css.staticPageHeader]: staticPageHeader,
  });

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  // Helper function for current page class (simplified version)
  const currentPageClassSimple = page => {
    return currentPage === page ? css.currentPage : null;
  };

  const showEditApplication = ownListing?.attributes?.state === 'pendingApproval';

  const hospitalsLink = (
    <NamedLink
      name="HospitalsAndClinicsPage"
      className={classNames(css.navLink, {
        [css.active]: isHospitalsPage,
      })}
    >
      <FormattedMessage id="TopbarDesktop.hospitalsAndClinicsPage" />
    </NamedLink>
  );

  const doctorsLink = (
    <NamedLink
      name="DoctorsPage"
      className={classNames(css.navLink, {
        [css.active]: isDoctorsPage,
      })}
    >
      <FormattedMessage id="TopbarDesktop.doctorsLink" />
    </NamedLink>
  );

  const shouldShowOnboardingLink =
    isAuthenticated && (!isIndividualUser(currentUser) || showEditApplication) && !ownListing;

  const startOnboardingLink = (
    <NamedLink
      className={classNames(css.yourListingsLink, currentPageClass('startOnboardingPage'))}
      name="DoctorOnboardingPage"
    >
      <FormattedMessage
        id={
          ownListing
            ? 'TopbarDesktop.editOnboardingApplication'
            : 'TopbarDesktop.startOnboardingApplication'
        }
      />
    </NamedLink>
  );

  const companiesLink = (
    <NamedLink
      name="EmployersPage"
      className={classNames(css.navLink, {
        [css.active]: isCompaniesPage,
      })}
    >
      <FormattedMessage id="TopbarDesktop.companiesLink" />
    </NamedLink>
  );

  const aiCoachLink = (
    <NamedLink
      name="AiPage"
      className={classNames(css.navLink, {
        [css.active]: isAiPage,
      })}
    >
      <FormattedMessage id="TopbarDesktop.aiPageLink" />
    </NamedLink>
  );

  const loggedAiCoachLink = isAuthenticated && (
    <Button
      className={css.aiCoachBtn}
      onClick={() => {
        trackChatbotEvent(MIXPANEL_EVENTS?.AI_OPENED, {
          buttonClicked: intl.formatMessage({ id: 'TopbarDesktop.aiPage' }),
        });
        history.push(createResourceLocatorString('AIConsultPage', routeConfiguration(), {}, {}));
      }}
    >
      <Icons name="aiButtonStar" /> {intl.formatMessage({ id: 'TopbarDesktop.aiPage' })}
    </Button>
  );

  const healthLink = (
    <NamedLink
      name="HealthPlansPage"
      className={classNames(css.navLink, {
        [css.active]: isHealthPlansPage,
      })}
    >
      <FormattedMessage id="TopbarDesktop.heathLink" />
    </NamedLink>
  );

  const aboutLink = (
    <NamedLink
      name="AboutPage"
      className={classNames(css.navLink, {
        [css.active]: isAboutPage,
      })}
    >
      <FormattedMessage id="TopbarDesktop.aboutLink" />
    </NamedLink>
  );

  const loginLink = !isAuthenticatedOrJustHydrated && (
    <div
      role="button"
      className={classNames(css.buttonBase, {
        [css.loginLink]: !isHeaderSticky,
        [css.loginHeader]: isHeaderSticky,
      })}
      onClick={() => {
        trackLogInClicked(MIXPANEL_EVENTS?.LOGIN_OPENED);
        history.push(createResourceLocatorString('LoginPage', routeConfiguration(), {}, {}));
      }}
    >
      <FormattedMessage id="TopbarDesktop.logIn" />
    </div>
  );

  const dashboardLink = isAuthenticatedOrJustHydrated && (
    <NamedLink
      className={classNames(css.yourListingsLink, currentPageClass('DashboardPage'))}
      name="DashboardPage"
    >
      <FormattedMessage id="TopbarDesktop.dashboardLink" />
    </NamedLink>
  );

  const signInButton = !isAuthenticatedOrJustHydrated && (
    <div
      role="button"
      className={classNames(css.signInButton, css.logInButton)}
      onClick={() => {
        openSignIn({ defaultToLogin: true });
      }}
    >
      <FormattedMessage id="TopbarDesktop.logIn" />
    </div>
  );

  const handleWrittenMenuMouseEnter = () => {
    if (writtenMenuTimeout) {
      clearTimeout(writtenMenuTimeout);
      setWrittenMenuTimeout(null);
    }
    setIsWrittenMenuOpen(true);
  };

  const handleWrittenMenuMouseLeave = () => {
    const timeout = setTimeout(() => {
      setIsWrittenMenuOpen(false);
    }, 150); // Small delay to prevent flickering when moving between label and content
    setWrittenMenuTimeout(timeout);
  };

  const handleProfileMenuMouseEnter = () => {
    if (profileMenuTimeout) {
      clearTimeout(profileMenuTimeout);
      setProfileMenuTimeout(null);
    }
    setIsProfileMenuHoverOpen(true);
  };

  const handleProfileMenuMouseLeave = () => {
    const timeout = setTimeout(() => {
      setIsProfileMenuHoverOpen(false);
    }, 150); // Small delay to prevent flickering when moving between label and content
    setProfileMenuTimeout(timeout);
  };

  const isPremiumAccessResolved = Boolean(currentUser?.id);
  const allowPremiumNavigation = isPremiumAccessResolved
    ? hasPremiumContentAccess(currentUser)
    : true;

  const guardPremiumNavigation = ({ event, destination }) => {
    logTopbarPremiumAccess({
      destination,
      allowAccess: allowPremiumNavigation,
      resolved: isPremiumAccessResolved,
      userId: currentUser?.id?.uuid || currentUser?.id || null,
    });

    if (!isPremiumAccessResolved) {
      return true;
    }

    if (!allowPremiumNavigation) {
      event?.preventDefault?.();
      history.push('/paywall');
      return false;
    }

    return true;
  };

  const handleWrittenLinkClick = (event, trackingAction, destination) => {
    const canNavigate = guardPremiumNavigation({ event, destination });
    if (!canNavigate) {
      return;
    }

    if (trackingAction) {
      trackingAction();
    }
  };

  const writtenLinks = isAuthenticated && (
    <div
      className={css.writtenMenuContainer}
      onMouseEnter={handleWrittenMenuMouseEnter}
      onMouseLeave={handleWrittenMenuMouseLeave}
    >
      <div className={classNames(css.navLink, { [css.writtenMenuIsOpen]: isWrittenMenuOpen })}>
        <FormattedMessage id="TopbarDesktop.writtenLink" />
        <Icons name="downArrow" />
      </div>
      {isWrittenMenuOpen && (
        <div className={css.writtenSubLinks}>
          <div key="HormoneBalanceResourcePage" className={css.writtenSubMenuItem}>
            <a
              href="/written-guide/menopause"
              className={classNames(css.navLink, css.dropmenu)}
              onClick={e =>
                handleWrittenLinkClick(
                  e,
                  () =>
                    trackArticleCategoryClicked(
                      MIXPANEL_EVENTS?.ARTICLE_CATEGORY_NAV_CLICKED,
                      TOPBAR_GUIDE_OPTIONS?.Menopause
                    ),
                  '/written-guide/menopause'
                )
              }
            >
              <FormattedMessage id="TopbarDesktop.hormoneBalanceLink" />
            </a>
          </div>
          <div key="NutritionResourcePage" className={css.writtenSubMenuItem}>
            <a
              href="/written-guide/nutrition"
              className={classNames(css.navLink, css.dropmenu)}
              onClick={e =>
                handleWrittenLinkClick(
                  e,
                  () =>
                    trackArticleCategoryClicked(
                      MIXPANEL_EVENTS?.ARTICLE_CATEGORY_NAV_CLICKED,
                      TOPBAR_GUIDE_OPTIONS?.Nutrition
                    ),
                  '/written-guide/nutrition'
                )
              }
            >
              <FormattedMessage id="TopbarDesktop.nutritionTipsRecipesLink" />
            </a>
          </div>
          <div key="PsychologyResourcePage" className={css.writtenSubMenuItem}>
            <a
              href="/written-guide/psychology"
              className={classNames(css.navLink, css.dropmenu)}
              onClick={e =>
                handleWrittenLinkClick(
                  e,
                  () =>
                    trackArticleCategoryClicked(
                      MIXPANEL_EVENTS?.ARTICLE_CATEGORY_NAV_CLICKED,
                      TOPBAR_GUIDE_OPTIONS?.Psychology
                    ),
                  '/written-guide/psychology'
                )
              }
            >
              <FormattedMessage id="TopbarDesktop.psychologyHypnotherapyLink" />
            </a>
          </div>
          <div key="FertilityResourcePage" className={css.writtenSubMenuItem}>
            <a
              href="/written-guide/fertility"
              className={classNames(css.navLink, css.dropmenu)}
              onClick={e =>
                handleWrittenLinkClick(
                  e,
                  () =>
                    trackArticleCategoryClicked(
                      MIXPANEL_EVENTS?.ARTICLE_CATEGORY_NAV_CLICKED,
                      TOPBAR_GUIDE_OPTIONS?.Fertility
                    ),
                  '/written-guide/fertility'
                )
              }
            >
              <FormattedMessage id="TopbarDesktop.fertilityLink" />
            </a>
          </div>
        </div>
      )}
    </div>
  );

  const modulesLink = isAuthenticated && (
    <a href="/resources#modules" className={css.navLink}>
      <FormattedMessage id="TopbarDesktop.modulesLink" />
    </a>
  );

  const symptomsLink = isAuthenticated && (
    <a href="/resources#symptoms" name="MenopauseResourcePage" className={css.navLink}>
      <FormattedMessage id="TopbarDesktop.symptomsLink" />
    </a>
  );

  const wellnessProgramLink = isAuthenticated && (
    <div
      className={css.navLink}
      onClick={event => {
        const canNavigate = guardPremiumNavigation({
          event,
          destination: createResourceLocatorString(
            'WellnessProgramPage',
            routeConfiguration(),
            {},
            {}
          ),
        });
        if (!canNavigate) {
          return;
        }

        trackWellnessProgramClicked(MIXPANEL_EVENTS?.ALL_PROGRAMS_CLICKED, null, null);
        history.push(
          createResourceLocatorString('WellnessProgramPage', routeConfiguration(), {}, {})
        );
      }}
    >
      <FormattedMessage id="TopbarDesktop.wellnessProgramLink" />
    </div>
  );

  const freeIndividualLink = isAuthenticated && isArrayLength(sortWebinarEventsByDate(events)) && (
    <div
      className={css.navLink}
      onClick={() => {
        trackSessionsClicked(MIXPANEL_EVENTS?.EVENTS_NAV_CLICKED, null, null, null);
        history.push(createResourceLocatorString('EventsPage', routeConfiguration(), {}, {}));
      }}
    >
      <FormattedMessage id="TopbarDesktop.freeIndividualLink" />
    </div>
  );

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isPanelActive, setIsPanelActive] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const isWide = (viewport?.width || 0) >= 1728;
  const [externalTerm, setExternalTerm] = useState('');
  const [isLocaleOpen, setIsLocaleOpen] = useState(false);

  // Lock body scroll when search panel is active
  useEffect(() => {
    if (isPanelActive) {
      const prev = document.body.style.overflow;
      document.body.dataset.prevOverflow = prev;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = document.body.dataset.prevOverflow || '';
        delete document.body.dataset.prevOverflow;
      };
    }
  }, [isPanelActive]);
  const guestLocaleOpen = !isAuthenticated && isLocaleOpen;
  const searchArea = guestLocaleOpen || !isAuthenticated ? null : (
    <div className={css.headerSearchArea}>
      <GlobalSearch
        isExpanded={isWide}
        onOpenChange={open => setIsSearchOpen(open)}
        onPanelToggle={active => setIsPanelActive(active)}
        onValueChange={v => setSearchValue(v)}
        externalTerm={externalTerm}
      />
    </div>
  );

  const preLoginLinks = !isAuthenticated ? (
    <>
      {aiCoachLink}
      {healthLink}
      {hospitalsLink}
      {companiesLink}
      {doctorsLink}
      {aboutLink}
    </>
  ) : null;

  const profileMenu = authenticatedOnClientSide ? (
    <div
      className={css.profileMenuContainer}
      onMouseEnter={handleProfileMenuMouseEnter}
      onMouseLeave={handleProfileMenuMouseLeave}
    >
      <div
        className={classNames(css.profileMenuLabel, {
          [css.profileMenuIsOpen]: isProfileMenuHoverOpen,
        })}
      >
        <div className={css.profileAvatar}>{currentUser?.attributes?.profile?.abbreviatedName}</div>
      </div>
      {isProfileMenuHoverOpen && (
        <div className={css.profileMenuContent} ref={profileMenuContentRef}>
          <div key="closeButton" className={css.closeButtonItem}>
            <div className={css.profileMenuHeader}>
              <div className={css.userInfoSection}>
                <div className={css.userDetails}>
                  <div className={css.userName}>
                    {currentUser?.attributes?.profile?.firstName}{' '}
                    {currentUser?.attributes?.profile?.lastName}
                  </div>
                  <div className={css.userEmail}>{currentUser?.attributes?.email}</div>
                </div>
              </div>
            </div>
          </div>

          <div key="AccountSettingsPage" className={css.menuItem}>
            <NamedLink
              className={classNames(
                css.yourListingsLink,
                currentPageClassSimple('AccountSettingsPage')
              )}
              name="AccountSettingsPage"
            >
              <div className={css.menuItemContent}>
                <IconSettings className={css.menuItemIcon} />
                <FormattedMessage id="TopbarDesktop.settingsLink" />
              </div>
            </NamedLink>
          </div>

          {canAccessDashboard(currentUser) && (
            <div key="DashboardPage" className={css.menuItem}>
              <NamedLink
                className={classNames(
                  css.yourListingsLink,
                  currentPageClassSimple('DashboardPage')
                )}
                name="DashboardPage"
              >
                <div className={css.menuItemContent}>
                  <Icons name="dashboard2" />
                  <FormattedMessage id="TopbarDesktop.dashboardLink" />
                </div>
              </NamedLink>
            </div>
          )}

          {!isCorporateUser && (
            <div key="SubscriptionPage" className={classNames(css.menuItem, css.accordionItem)}>
              <div
                className={classNames(css.accordionHeader, css.yourListingsLink)}
                onClick={handleSubscriptionToggle}
              >
                <div className={css.menuItemContent}>
                  <IconShield className={css.menuItemIcon} />
                  <FormattedMessage id="TopbarDesktop.mySubscriptionPage" />
                </div>
                <div
                  className={classNames(css.accordionIcon, {
                    [css.accordionIconExpanded]: isSubscriptionExpanded,
                  })}
                >
                  <IconAccordion />
                </div>
              </div>
              {isSubscriptionExpanded && (
                <div className={css.accordionContent}>
                  <div key="SubscriptionPage" className={css.subMenuItem}>
                    <NamedLink
                      name="SubscriptionPage"
                      className={classNames(
                        css.yourListingsLink,
                        currentPageClassSimple('SubscriptionPage')
                      )}
                    >
                      <FormattedMessage id="TopbarDesktop.subscriptionPage" />
                    </NamedLink>
                  </div>
                  <div key="MyInvoicesPage" className={css.subMenuItem}>
                    <NamedLink
                      name="MyInvoicesPage"
                      className={classNames(
                        css.yourListingsLink,
                        currentPageClassSimple('MyInvoicesPage')
                      )}
                    >
                      <FormattedMessage id="TopbarDesktop.myInvoicesPage" />
                    </NamedLink>
                  </div>
                </div>
              )}
            </div>
          )}

          <div key="FavoritesPage" className={css.menuItem}>
            <NamedLink
              name="FavoritesPage"
              className={classNames(css.yourListingsLink, currentPageClassSimple('FavoritesPage'))}
            >
              <div className={css.menuItemContent}>
                <IconHeart className={css.menuItemIcon} />
                <FormattedMessage id="TopbarDesktop.myFavorites" />
              </div>
            </NamedLink>
          </div>

          <div key="WatchedVideosPage" className={css.menuItem}>
            <NamedLink
              name="WatchedVideosPage"
              className={classNames(
                css.yourListingsLink,
                currentPageClassSimple('WatchedVideosPage')
              )}
            >
              <div className={css.menuItemContent}>
                <IconWatched className={css.menuItemIcon} />
                <FormattedMessage id="TopbarDesktop.Watched" />
              </div>
            </NamedLink>
          </div>

          <div
            key="MyTrackPage"
            className={css.menuItem}
            onClick={() => {
              trackMoodSymptomHabitEvent(MIXPANEL_EVENTS?.HABIT_TRACKING_OPENED);
            }}
          >
            <NamedLink
              name="MyTrackPage"
              className={classNames(css.yourListingsLink, currentPageClass('MyTrackPage'))}
            >
              <div className={css.menuItemContent}>
                <IconSymptomsTracker className={css.menuItemIcon} />
                <FormattedMessage id="Resource.habit.habitTrackTitle" />
              </div>
            </NamedLink>
          </div>

          <div key="logout" className={css.menuItem}>
            <InlineTextButton
              rootClassName={css.logoutButton}
              onClick={() => {
                trackLogOutClicked();
                onLogout();
              }}
            >
              <div className={css.menuItemContent}>
                <IconLogout className={css.menuItemIcon} />
                <FormattedMessage id="TopbarDesktop.logout" />
              </div>
            </InlineTextButton>
          </div>

          <div key="divider" className={css.dividerItem}>
            <div className={css.divider}></div>
          </div>

          <div key="languageSection" className={css.languageSection}>
            <div className={css.languageSectionHeader}>
              <FormattedMessage id="TopbarDesktop.languages" />
            </div>
            <div className={css.languageButtons}>
              <button
                className={classNames(css.languageButton, {
                  [css.languageButtonActive]: appConfig.localization.locale === 'en',
                })}
                onClick={() => handleLanguageChange('en')}
              >
                <img src={englishImage} alt="English" className={css.languageFlag} />
                <span>EN</span>
              </button>
              <button
                className={classNames(css.languageButton, {
                  [css.languageButtonActive]: appConfig.localization.locale === 'el',
                })}
                onClick={() => handleLanguageChange('el')}
              >
                <img src={greekImage} alt="Greek" className={css.languageFlag} />
                <span>GR</span>
              </button>
              <button
                className={classNames(css.languageButton, {
                  [css.languageButtonActive]: appConfig.localization.locale === 'de',
                })}
                onClick={() => handleLanguageChange('de')}
              >
                <img src={germanImage} alt="German" className={css.languageFlag} />
                <span>DE</span>
              </button>
              {/* <button
                className={classNames(css.languageButton, {
                  [css.languageButtonActive]: appConfig.localization.locale === 'ar',
                })}
                onClick={() => handleLanguageChange('ar')}
              >
                <span className={css.languageFlag}>
                  <Icons name="globeIcon" />
                </span>
                <span>AR</span>
              </button> */}
            </div>
          </div>
        </div>
      )}
    </div>
  ) : null;

  const postApprorvalProfileMenu = authenticatedOnClientSide ? (
    <div
      className={css.profileMenuContainer}
      onMouseEnter={handleProfileMenuMouseEnter}
      onMouseLeave={handleProfileMenuMouseLeave}
    >
      <div
        className={classNames(css.profileMenuLabel, {
          [css.profileMenuIsOpen]: isProfileMenuHoverOpen,
        })}
      >
        <div className={css.profileAvatar}>{currentUser?.attributes?.profile?.abbreviatedName}</div>
      </div>
      {isProfileMenuHoverOpen && (
        <div className={css.profileMenuContent}>
          <div key="closeButton" className={css.closeButtonItem}>
            <div className={css.profileMenuHeader}>
              <div className={css.userInfoSection}>
                <div className={css.userAvatar}>
                  {currentUser?.attributes?.profile?.abbreviatedName}
                </div>
                <div className={css.userDetails}>
                  <div className={css.userName}>
                    {currentUser?.attributes?.profile?.firstName}{' '}
                    {currentUser?.attributes?.profile?.lastName}
                  </div>
                  <div className={css.userEmail}>{currentUser?.attributes?.email}</div>
                </div>
              </div>
              <button className={css.closeButton} onClick={handleCloseProfileMenu}>
                <IconClose />
              </button>
            </div>
          </div>

          {shouldShowOnboardingLink && !ownListing && (
            <div key="StartOnboardingPage" className={css.menuItem}>
              {startOnboardingLink}
            </div>
          )}

          {canAccessDashboard(currentUser) && (
            <div key="DashboardPage" className={css.menuItem}>
              {dashboardLink}
            </div>
          )}

          <div key="ManageListingsPage" className={css.menuItem}>
            <div className={css.profileMenuHead}></div>
          </div>

          <div key="EditListingPage" className={css.menuItem}>
            {ownListing ? (
              <NamedLink
                name="EditListingPage"
                params={{
                  id: ownListing?.id?.uuid || draftId,
                  slug: createSlug(ownListing?.attributes?.title || draftSlug),
                  type: ownListing ? 'edit' : 'new',
                  tab: 'details',
                }}
                className={classNames(css.yourListingsLink, currentPageClass('EditListingPage'))}
              >
                <FormattedMessage id="TopbarDesktop.editProfile" />
              </NamedLink>
            ) : (
              <NamedLink
                name="DoctorOnboardingPage"
                className={classNames(
                  css.yourListingsLink,
                  currentPageClass('DoctorOnboardingPage')
                )}
              >
                <FormattedMessage id="TopbarDesktop.completeProfile" />
              </NamedLink>
            )}
          </div>

          <div key="InboxPage" className={css.menuItem}>
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('InboxPage'))}
              name="InboxPage"
              params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
            >
              <div className={css.menuItemContent}>
                <IconSessions className={css.menuItemIcon} />
                <FormattedMessage id="TopbarDesktop.inbox" />
              </div>
            </NamedLink>
          </div>

          <div key="AccountSettingsPage" className={css.menuItem}>
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <div className={css.menuItemContent}>
                <IconSettings className={css.menuItemIcon} />
                <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
              </div>
            </NamedLink>
          </div>

          <div key="logout" className={css.menuItem}>
            <InlineTextButton
              rootClassName={css.logoutButton}
              onClick={() => {
                trackLogOutClicked();
                onLogout();
              }}
            >
              <div className={css.menuItemContent}>
                <IconLogout className={css.menuItemIcon} />
                <FormattedMessage id="TopbarDesktop.logout" />
              </div>
            </InlineTextButton>
          </div>

          <div key="divider" className={css.dividerItem}>
            <div className={css.divider}></div>
          </div>

          <div key="languageSection" className={css.languageSection}>
            <div className={css.languageSectionHeader}>
              <FormattedMessage id="TopbarDesktop.languages" />
            </div>
            <div className={css.languageButtons}>
              <button
                className={classNames(css.languageButton, {
                  [css.languageButtonActive]: appConfig.localization.locale === 'en',
                })}
                onClick={() => handleLanguageChange('en')}
              >
                <img src={englishImage} alt="English" className={css.languageFlag} />
                <span>EN</span>
              </button>
              <button
                className={classNames(css.languageButton, {
                  [css.languageButtonActive]: appConfig.localization.locale === 'el',
                })}
                onClick={() => handleLanguageChange('el')}
              >
                <img src={greekImage} alt="Greek" className={css.languageFlag} />
                <span>GR</span>
              </button>
              <button
                className={classNames(css.languageButton, {
                  [css.languageButtonActive]: appConfig.localization.locale === 'de',
                })}
                onClick={() => handleLanguageChange('de')}
              >
                <img src={germanImage} alt="German" className={css.languageFlag} />
                <span>DE</span>
              </button>
              <button
                className={classNames(css.languageButton, {
                  [css.languageButtonActive]: appConfig.localization.locale === 'ar',
                })}
                onClick={() => handleLanguageChange('ar')}
              >
                <span className={css.languageFlag}>
                  <Icons name="globeIcon" />
                </span>
                <span>AR</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  ) : null;

  const renderLinkedLogo = (
    <LinkedLogo
      isAuthenticated={isAuthenticated}
      className={css.logoLink}
      format="desktop"
      alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
      logoVariant={logoVariant}
    />
  );

  const profileUI = isStripeConnected ? postApprorvalProfileMenu : profileMenu;

  return (
    <>
      {mounted ? (
        staticPageHeader ? (
          <div
            className={classNames(
              isFullWidthHeader ? css.fullWidthContainer : css.fixedWidthContainer,
              dashboardHeader ? css.dashboardHeader : null,
              headerWithoutShadow ? css.headerWithoutShadow : null
            )}
          >
            <nav className={classNames(classes, { [css.searchOpen]: isSearchOpen && !isWide })}>
              {renderLinkedLogo}
              <div className={css.middleLinks}>
                {!isSearchOpen || isWide ? preLoginLinks : null}
              </div>
              <div className={css.rightLinks}>
                <div className={css.rightControls}>
                  {searchArea}
                  {!isSearchOpen || isWide ? (
                    <>
                      {!isAuthenticated && (
                        <LocaleMenu
                          className={css.languageButton}
                          compact
                          isOpen={isLocaleOpen}
                          onToggleActive={open => {
                            setIsLocaleOpen(open);
                            if (open) {
                              setIsSearchOpen(false);
                              setIsPanelActive(false);
                            }
                          }}
                        />
                      )}
                      {loginLink}
                    </>
                  ) : null}
                </div>
                {profileUI}
              </div>
            </nav>
            {isPanelActive ? (
              <div
                className={css.screenOverlay}
                onClick={() => {
                  setIsPanelActive(false);
                  setIsSearchOpen(false);
                }}
                aria-hidden="true"
              />
            ) : null}
          </div>
        ) : (
          <div
            className={classNames(
              isFullWidthHeader ? css.fullWidthContainer : css.fixedWidthContainer,
              dashboardHeader ? css.dashboardHeader : null
            )}
          >
            <nav
              className={classNames(classes, {
                [css.searchOpen]: isSearchOpen && !isWide,
                [css.expandedHeaderNav]: isPanelActive,
              })}
            >
              {renderLinkedLogo}
              <div className={css.middleLinks}>
                {loggedAiCoachLink}
                {preLoginLinks}
                {freeIndividualLink}
                {symptomsLink}
                {modulesLink}
                {writtenLinks}
                {wellnessProgramLink}
              </div>
              <div className={css.rightLinks}>
                <div className={css.rightControls}>
                  {searchArea}
                  <LocaleMenu
                    className={css.languageButton}
                    compact
                    isOpen={isLocaleOpen}
                    onToggleActive={open => {
                      setIsLocaleOpen(open);
                      if (open) {
                        setIsSearchOpen(false);
                        setIsPanelActive(false);
                      }
                    }}
                  />
                  {signInButton}
                </div>
                {profileUI}
              </div>
            </nav>
            {isPanelActive ? (
              <div className={css.expandedHeaderPanel} data-header-search-panel="true">
                <SearchPanel
                  value={searchValue}
                  onSelect={it => {
                    if (it?.label) {
                      setExternalTerm(it.label);
                      setSearchValue(it.label);
                      setIsSearchOpen(true);
                      setIsPanelActive(true);
                    }
                  }}
                />
              </div>
            ) : null}
            {isPanelActive ? (
              <div
                className={css.screenOverlay}
                onClick={() => {
                  setIsPanelActive(false);
                  setIsSearchOpen(false);
                }}
                aria-hidden="true"
              />
            ) : null}
          </div>
        )
      ) : null}
    </>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  appConfig: null,
  logoVariant: null,
  events: [],
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  appConfig: object,
  logoVariant: string,
  events: array,
  viewport: object,
};

export default TopbarDesktop;
